/**
 * Chat API Service
 * Handles multi-AI property chat requests with consensus
 */

/**
 * Send a chat message about a property to the multi-AI chat endpoint
 * @param {string} internalId - Order internal ID
 * @param {string} question - User's question
 * @param {string} sessionId - Optional chat session ID
 * @param {string} userId - Optional user ID
 * @returns {Promise<Object>} Chat response with consensus and model details
 */
export async function sendChatMessage(internalId, question, sessionId = null, userId = 'user') {
  try {
    const requestBody = {
      internalId,
      question,
      userId
    };

    // Include sessionId if provided (for conversation continuity)
    if (sessionId) {
      requestBody.sessionId = sessionId;
    }

    const response = await fetch('https://sunny-analysis.messagesimproved.com/property-chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Chat request failed: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[CHAT API] Error:', error);
    throw error;
  }
}

/**
 * Get chat history for a session
 * @param {string} sessionId - Chat session ID
 * @returns {Promise<Array>} Array of chat messages
 */
export async function getChatHistory(sessionId) {
  try {
    // This endpoint would need to be implemented on the backend
    const response = await fetch(`https://sunny-analysis.messagesimproved.com/chat-history/${sessionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch chat history: ${response.status}`);
    }

    const data = await response.json();
    return data.messages || [];
  } catch (error) {
    console.error('[CHAT API] Error fetching history:', error);
    // Return empty array if history not available
    return [];
  }
}

/**
 * Format model response for display
 * @param {Object} modelResponse - Individual model response object
 * @returns {string} Formatted string for UI
 */
export function formatModelResponse(modelResponse) {
  if (!modelResponse) return 'No response';

  const { model, tokens, latency } = modelResponse;
  return `${model} (${tokens} tokens, ${latency?.toFixed(2)}s)`;
}

/**
 * Calculate confidence level text
 * @param {number} confidence - Confidence score (0.0 - 1.0)
 * @returns {Object} Text and color for display
 */
export function getConfidenceDisplay(confidence) {
  if (confidence >= 0.8) {
    return { text: 'High Confidence', color: '#28a745' };
  } else if (confidence >= 0.6) {
    return { text: 'Medium Confidence', color: '#ffc107' };
  } else {
    return { text: 'Low Confidence', color: '#dc3545' };
  }
}
